import React from "react";
import { useParams } from "react-router-dom";
import useApiEp from "../../hooks/useApiEp";
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import { ContentCopy, Check } from "@mui/icons-material";
import MuPb from "../../widgets/MuPb";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";

const MessageDebugDetail = () => {
  const { messageId, cusId } = useParams();
  const [copied, setCopied] = React.useState(false);

  const { data, isLoading, isError, error } = useApiEp(
    `/root/get-debug-curl-message/${cusId}/${messageId}`
  );

  // Handle copying to clipboard
  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Generate cURL string
  const generateCurlString = (data) => {
    if (!data?.client || !data?.message) return null;

    const { phoneNumberId, haptikToken, wabaAccountId } = data.client;
    const { payload } = data.message;

    return `curl --location 'https://amped-express.interakt.ai/api/v23.0/${phoneNumberId}/messages' \\
--header 'x-access-token: ${haptikToken}' \\
--header 'x-waba-id: ${wabaAccountId}' \\
--header 'Content-Type: application/json' \\
--data '${JSON.stringify(payload, null, 2)}'`;
  };

  if (isLoading) {
    return <MuPb />;
  }

  if (isError) {
    return (
      <Container sx={{ mt: 3 }}>
        <Alert severity="error">
          {error?.message || "Failed to load debug message."}
        </Alert>
      </Container>
    );
  }

  const curlString = generateCurlString(data);

  if (!curlString) {
    return (
      <Container sx={{ mt: 3 }}>
        <Alert severity="warning">
          Invalid or incomplete debug data received.
        </Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 3, mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" component="h1">
          Debug cURL Message
        </Typography>

        <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
          <IconButton
            onClick={() => handleCopy(curlString)}
            color={copied ? "success" : "primary"}
            size="small"
          >
            {copied ? <Check /> : <ContentCopy />}
          </IconButton>
        </Tooltip>
      </Box>

      <Paper elevation={3} sx={{ overflow: "hidden", borderRadius: 2 }}>
        <SyntaxHighlighter
          language="bash"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: "20px",
            fontSize: "0.9rem",
            lineHeight: 1.6,
          }}
          wrapLongLines={true}
          showLineNumbers={false}
        >
          {curlString}
        </SyntaxHighlighter>
      </Paper>

      {/* Optional: Display metadata */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Message ID: {messageId} | Customer ID: {cusId}
        </Typography>
      </Box>
    </Container>
  );
};

export default MessageDebugDetail;
